import { useRollbar } from '@rollbar/react'
import { RedirectionTypesEnum } from 'common/enums/ButtonActionEnum'
import { BadRequest, NetworkError, NotFound } from 'common/errors'
import { getLocalTimeZone } from 'common/utils/date-utils'
import useManagement, {
  selectors as managementSelectors,
} from 'publisher/hooks/useManagement'
import { typedPageSelectors, usePage as useTypedPage } from 'publisher/store'
import { validateEmptyOptInFields } from 'publisher/utils/fieldValidation'
import { redirectTo } from 'publisher/utils/redirect'
import { useState } from 'react'
import { formOptIn } from 'src/publisher/api/form-opt-in-api'
import { ContactSlugEnum } from 'src/shared/enums/contact-fields-enum'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import {
  FormInterface,
  FormSubEntityTypeEnum,
} from 'src/shared/types/entities/form-interface'
import { CheckboxesErrorsType, useFormContext } from '../context/form-context'
import { convertFormFieldValuesToFormFieldApi } from '../utils/convert-form-field-values-to-form-field-api'

export interface UseOptInSubmitFormInterface {
  formEntity: FormInterface
}

export const useOptInSubmitForm = ({
  formEntity,
}: UseOptInSubmitFormInterface) => {
  const { t } = useTypedTranslation()

  const rollbar = useRollbar()

  const [isLoading, setLoading] = useState(false)
  const [isSuccess, setSuccess] = useState(false)

  const {
    fieldsValues,
    checkboxesValues,
    checkboxesErrors,
    setCheckboxesErrors,
    commonErrors,
    setCommonErrors,
    setFieldsErrors,
  } = useFormContext()

  const isTemplate = useTypedPage(typedPageSelectors.getIsPageTemplate)
  const isPreview = useManagement(managementSelectors.isPreviewMode)

  const onSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()

    setCommonErrors([])
    setSuccess(false)

    if (!isTemplate && !isPreview) {
      const checkboxes = formEntity.subEntities.filter(
        subEntity => subEntity.type === FormSubEntityTypeEnum.Checkbox,
      )

      const checkboxesErrors = checkboxes.reduce((errors, checkbox) => {
        if (!checkbox.optional) {
          if (checkbox.id in checkboxesValues) {
            if (!checkboxesValues[checkbox.id]) {
              errors[checkbox.id] = checkbox.errorText
            }
          } else {
            errors[checkbox.id] = checkbox.errorText
          }
        }
        return errors
      }, {} as CheckboxesErrorsType)
      setCheckboxesErrors(checkboxesErrors)

      if (!Object.keys(checkboxesErrors).length) {
        const emptyFieldErrors = validateEmptyOptInFields(
          formEntity.subEntities
            .filter(subEntity => subEntity.type === FormSubEntityTypeEnum.Field)
            .reduce(
              (acc, subEntity) => {
                acc[subEntity.slug] = {
                  value: fieldsValues[subEntity.slug],
                  optional: subEntity.optional,
                }
                return acc
              },
              {} as Record<
                ContactSlugEnum,
                { value: string | undefined; optional: boolean }
              >,
            ),
          t,
        )
        if (Object.keys(emptyFieldErrors).length) {
          setFieldsErrors(emptyFieldErrors)
        } else {
          setLoading(true)
          try {
            const { data } = await formOptIn({
              fields: convertFormFieldValuesToFormFieldApi(fieldsValues),
              formId: formEntity.id,
              timezone: getLocalTimeZone(),
            })
            if (
              formEntity.action.redirectionType ===
                RedirectionTypesEnum.customUrl &&
              formEntity.action.urlRedirect
            ) {
              redirectTo(formEntity.action.urlRedirect)
            } else if (
              formEntity.action.redirectionType ===
                RedirectionTypesEnum.nextStep &&
              data.redirect
            ) {
              redirectTo(data.redirect)
            }

            // biome-ignore lint/suspicious/noConsole: <explanation>
            console.log('fieldsValues', fieldsValues)
            setSuccess(true)
          } catch (error) {
            if (error instanceof BadRequest) {
              setCommonErrors(error.response.data.errors.common)
              setFieldsErrors(error.response.data.errors.fields)
              window.dispatchEvent(new Event('form_updated'))
            } else if (error instanceof NetworkError) {
              setCommonErrors([t('core.errors.no_connection')])
            } else if (error instanceof NotFound) {
              setCommonErrors([t('page Not Found')])
            } else {
              rollbar.error(`Form opt-in error`, error as Error)
            }

            setSuccess(false)
          }
          setLoading(false)
        }
      }
    }
  }
  return { isLoading, checkboxesErrors, commonErrors, onSubmit, isSuccess }
}
